// app.operators — manage a project's OPERATORS (the "server users": platform
// identities allowed into this project's `/server`). Distinct from `app.users`,
// which manages this project's own end-users in the project pool.
//
// Operators live as `{slug}:operator-owner` / `{slug}:operator-admin` groups in
// the PLATFORM pool (`console:owner`/`console:admin` — the ROOT project's own
// groups — for the platform itself),
// so they can't be edited with the project-scoped STS the rest of `/server` uses
// (Cognito group admin can't be IAM-scoped by group name). Instead this module
// reaches the platform-side `manage-operators` broker: it mints platform STS via
// the platform Identity Pool, enqueues the broker on the platform bucket, and
// passes the id_token so the broker verifies the caller server-side and only
// mutates `{slug}:*`. The browser never holds platform-pool admin.

import { S3Client } from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import type { OperatorRole } from "./policy-templates.ts";
import { ROOT_SLUG, operatorSlugsFromGroups } from "./conventions.ts";
import { paths } from "./paths.ts";
import { createInvoker } from "./invoker.ts";
import type { JwtClaims } from "./oidc.ts";
import { createCalls } from "./calls.ts";
import { getCredentialsViaIdentityPool } from "./sts.ts";

export interface Operator {
  /** Platform-account email. */
  email: string;
  /** One of the FIXED operator roles (see OPERATOR_ROLES) — the root
   *  project only ever reports owner/admin. */
  role: OperatorRole;
}

/** Target a DIFFERENT project than the module's own. The broker verifies the
 *  caller's platform groups server-side either way — passing a slug you don't
 *  operate just gets refused there. A multi-project console (the platform
 *  registry) uses this instead of re-encoding the broker's wire shape. */
export interface OperatorTargetOpts {
  slug?: string;
}

export interface OperatorsModule {
  /** List a project's operators across all fixed roles. Defaults to this
   *  project; pass `{ slug }` to ask about another one you operate. */
  list(opts?: OperatorTargetOpts): Promise<Operator[]>;
  /** Grant a platform account an operator role (one of the FIXED set —
   *  owner/admin/developer/viewer; the capability bundles are platform-
   *  defined, not per-project). The account must already exist on the
   *  platform. Defaults to `admin`. A grant is a MOVE: the target holds
   *  exactly one operator role afterwards. */
  grant(email: string, role?: OperatorRole, opts?: OperatorTargetOpts): Promise<void>;
  /** Revoke a platform account's operator access (removes every role). */
  revoke(email: string, opts?: OperatorTargetOpts): Promise<void>;
  /** Invoke any PLATFORM-runtime function with the operator's platform
   *  credentials — the generic form of the manage-operators transport.
   *  Used by role sync for the identity-pool mapping step (browser creds
   *  can't call cognito-identity; see sync-role-mappings). */
  invokePlatform(name: string, input: Record<string, unknown>): Promise<unknown>;
  /** The slugs the CURRENT session can operate, straight from the token's
   *  groups (the same groups the Identity Pools map to roles — if it's not
   *  here, credentials would be denied anyway). No network call. */
  myProjects(): string[];
}

export interface OperatorsConfig {
  region: string;
  slug: string;
  /** The pool/identity-pool/bucket that fronts the broker. For a child project
   *  this is the PLATFORM pool (operator federation); for the platform project
   *  itself it's its OWN pool (the broker runs in the platform runtime either
   *  way and manages `{slug}:operator-*` in the platform pool). */
  consoleAuth: { poolId: string; identityPoolId: string; bucket: string };
  /** Current id_token (the `/server` OIDC session). */
  getIdToken: () => string | null;
  getClaims: () => JwtClaims | null;
}

export function createOperators(cfg: OperatorsConfig): OperatorsModule {
  /** PLATFORM-scoped STS: exchange the operator's platform id_token at the
   *  PLATFORM identity pool. These creds carry their platform role — which is
   *  what lets them invoke the ROOT project's Lambdas (the broker lives there). */
  async function platformCreds() {
    const idToken = cfg.getIdToken();
    if (!idToken) throw new Error("operators: not signed in");
    return getCredentialsViaIdentityPool({
      region: cfg.region,
      identityPoolId: cfg.consoleAuth.identityPoolId,
      userPoolId: cfg.consoleAuth.poolId,
      idToken,
    });
  }

  async function platformS3(): Promise<S3Client> {
    const creds = await platformCreds();
    return new S3Client({
      region: cfg.region,
      forcePathStyle: true,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      },
      systemClockOffset: 0,
    });
  }

  // The broker is a function of the ROOT project (`s3p-{ROOT_SLUG}-fn-manage-
  // operators`), not of this project — so the invoker is pointed at the root
  // slug and signed with PLATFORM creds. (It used to enqueue by writing the
  // platform bucket's inbox; that transport is gone.)
  const invoker = createInvoker({
    region: cfg.region,
    slug: ROOT_SLUG,
    getCredentials: platformCreds,
    getIdToken: cfg.getIdToken,
  });

  const calls = createCalls({
    bucket: cfg.consoleAuth.bucket,
    bucketPrefix: "",
    paths: paths(),
    getS3Client: platformS3,
    getClaims: cfg.getClaims,
    enqueue: (name, input, taskId) => invoker.enqueue(name, input, taskId),
  });

  async function callBroker(
    action: "list" | "grant" | "revoke",
    slug: string,
    extra: Record<string, unknown>,
  ): Promise<unknown> {
    if (!cfg.getIdToken()) throw new Error("operators: not signed in");
    // NOTE: the id_token is deliberately NOT in the input. The invoker sends it
    // in the Lambda payload (never persisted), whereas `input` is copied into the
    // inflight record at .s3p/functions/invocations/{sub}/ — which its owner can
    // signed-in user READ. Passing it here (as this did) parked a live operator
    // token in the bucket for every member to lift.
    return calls.invoke("manage-operators", { action, slug, ...extra }, { timeoutMs: 30_000 });
  }

  return {
    async list(opts = {}) {
      const r = (await callBroker("list", opts.slug ?? cfg.slug, {})) as {
        operators?: Operator[];
      };
      return r.operators ?? [];
    },
    async grant(email, role = "admin", opts = {}) {
      await callBroker("grant", opts.slug ?? cfg.slug, { targetEmail: email, role });
    },
    async revoke(email, opts = {}) {
      await callBroker("revoke", opts.slug ?? cfg.slug, { targetEmail: email });
    },
    async invokePlatform(name, input) {
      if (!cfg.getIdToken()) throw new Error("operators: not signed in");
      // Same id_token posture as callBroker: the invoker carries it in the
      // Lambda payload, never in the persisted inflight record.
      return calls.invoke(name, input, { timeoutMs: 60_000 });
    },
    myProjects() {
      const claims = cfg.getClaims();
      // Same claim fallback as deriveMe: Cognito's `cognito:groups`, else the
      // legacy `groups` claim.
      const raw = claims?.["cognito:groups"] ?? claims?.groups ?? [];
      return operatorSlugsFromGroups(
        Array.isArray(raw) ? raw.filter((g): g is string => typeof g === "string") : [],
      );
    },
  };
}
